// ============================================
// FILE: src/components/fashion/ProductReviews.jsx
// ============================================
//
// Ratings summary, distribution bars, filterable review list, and the
// write-review entry point.
//
// The summary always reflects every published review — the API returns it
// separately from the paginated page, so filtering to 1-star does not make the
// headline average appear to change.

import React, { useState, useEffect, useCallback } from 'react';
import { ThumbsUp, BadgeCheck, Pencil, Trash2, ImageIcon } from 'lucide-react';
import StarRating from './StarRating';
import WriteReviewModal from './WriteReviewModal';
import MediaLightbox from './MediaLightbox';
import { resolveProductImageUrl } from '../../utils/imageHelpers';
import apiService from '../../apiService';

const SORTS = [
  { value: 'recent',  label: 'Most Recent' },
  { value: 'helpful', label: 'Most Helpful' },
  { value: 'high',    label: 'Highest Rated' },
  { value: 'low',     label: 'Lowest Rated' },
];

const ProductReviews = ({
  product,
  isAuthenticated = false,
  onRequireLogin = null,
  showToast,
  isDesktop = false,
  onSummaryChange = () => {},
}) => {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('recent');
  const [starFilter, setStarFilter] = useState(null);
  const [photosOnly, setPhotosOnly] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [showWrite, setShowWrite] = useState(false);
  const [lightbox, setLightbox] = useState(null);   // { media: [], index }
  const [helpfulVoted, setHelpfulVoted] = useState({});

  const productId = product?.id;

  const load = useCallback(async (targetPage = 1, append = false) => {
    if (!productId) return;
    setLoading(true);

    const resp = await apiService.getProductReviews(productId, {
      page: targetPage,
      per_page: 10,
      sort,
      rating: starFilter,
      with_photos: photosOnly,
    });

    const rows = resp?.data?.reviews?.data || [];
    setReviews((current) => (append ? [...current, ...rows] : rows));
    setLastPage(resp?.data?.reviews?.last_page || 1);
    setPage(targetPage);

    if (resp?.data?.summary) {
      setSummary(resp.data.summary);
      onSummaryChange(resp.data.summary);
    }

    setLoading(false);
    // onSummaryChange is a render-scoped callback in the parent; including it
    // would re-run this on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, sort, starFilter, photosOnly]);

  useEffect(() => { load(1, false); }, [load]);

  useEffect(() => {
    if (!isAuthenticated || !productId) {
      setEligibility(null);
      return;
    }
    let cancelled = false;
    apiService.getReviewEligibility(productId).then((r) => {
      if (!cancelled) setEligibility(r?.data || null);
    });
    return () => { cancelled = true; };
  }, [isAuthenticated, productId]);

  const total = summary?.total ?? 0;

  const handleWriteClick = () => {
    if (!isAuthenticated) {
      onRequireLogin ? onRequireLogin() : showToast?.('Please sign in to write a review', 'warning');
      return;
    }
    if (eligibility && !eligibility.can_review && !eligibility.has_reviewed) {
      showToast?.('Only customers who have received this product can review it', 'warning');
      return;
    }
    setShowWrite(true);
  };

  const handleSaved = async () => {
    const fresh = await apiService.getReviewEligibility(productId);
    setEligibility(fresh?.data || null);
    load(1, false);
  };

  const handleDelete = async (review) => {
    if (!window.confirm('Delete your review?')) return;
    const resp = await apiService.deleteReview(productId, review.id);
    if (resp?.success) {
      showToast?.('Review deleted', 'success');
      handleSaved();
    } else {
      showToast?.('Could not delete review', 'error');
    }
  };

  const handleHelpful = async (review) => {
    if (helpfulVoted[review.id]) return;
    if (!isAuthenticated) {
      onRequireLogin ? onRequireLogin() : showToast?.('Please sign in first', 'warning');
      return;
    }

    setHelpfulVoted((v) => ({ ...v, [review.id]: true }));
    const resp = await apiService.markReviewHelpful(productId, review.id);

    if (resp?.success) {
      setReviews((current) => current.map((r) =>
        r.id === review.id ? { ...r, helpful_count: resp.data.helpful_count } : r));
    }
  };

  const openPhotos = (review, index) => {
    setLightbox({
      media: review.images.map((img) => ({
        id: img.id,
        media_type: 'image',
        url: resolveProductImageUrl(img.image_url),
        large_url: resolveProductImageUrl(img.image_url),
        thumbnail_url: resolveProductImageUrl(img.thumbnail_url),
        alt_text: `Customer photo`,
      })),
      index,
    });
  };

  const myReview = eligibility?.my_review || null;

  return (
    <section className="mt-10 pt-8" style={{ borderTop: '1px solid var(--fx-line)' }}>
      <h3 className="fx-label mb-5">Ratings &amp; Reviews</h3>

      {/* ---- Summary ---- */}
      <div className={`${isDesktop ? 'flex items-start gap-10' : ''} mb-6`}>
        <div className={isDesktop ? 'flex-shrink-0 text-center' : 'flex items-center gap-5 mb-5'}>
          <div>
            <div className="flex items-baseline gap-1 justify-center">
              <span style={{ fontSize: 40, fontWeight: 700, lineHeight: 1 }}>
                {total > 0 ? Number(summary.average).toFixed(1) : '—'}
              </span>
              <span style={{ fontSize: 18, color: 'var(--fx-ink-3)' }}>/5</span>
            </div>
            <div className="mt-2 flex justify-center">
              <StarRating value={summary?.average || 0} size={16} />
            </div>
            <p className="mt-1.5" style={{ fontSize: 13, color: 'var(--fx-ink-2)' }}>
              {total} {total === 1 ? 'rating' : 'ratings'}
            </p>
          </div>
        </div>

        {/* Distribution — each bar filters the list */}
        <div className={isDesktop ? 'flex-1 max-w-sm' : ''}>
          {(summary?.distribution || []).map(({ rating, count }) => {
            const pct = total > 0 ? (count / total) * 100 : 0;
            const active = starFilter === rating;

            return (
              <button
                key={rating}
                type="button"
                onClick={() => setStarFilter(active ? null : rating)}
                disabled={count === 0}
                className="w-full flex items-center gap-2.5 py-1 group"
                style={{ cursor: count === 0 ? 'default' : 'pointer' }}
              >
                <span style={{ fontSize: 13, width: 12, color: active ? 'var(--fx-pink)' : 'var(--fx-ink-2)' }}>
                  {rating}
                </span>
                <StarRating value={1} size={11} />
                <span className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: '#eaeaec' }}>
                  <span
                    className="block h-full rounded-full"
                    style={{ width: `${pct}%`, background: active ? 'var(--fx-pink)' : 'var(--fx-green)' }}
                  />
                </span>
                <span style={{ fontSize: 12, color: 'var(--fx-ink-3)', width: 28, textAlign: 'right' }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ---- Write / edit CTA ---- */}
      <div className="mb-6">
        {myReview ? (
          <div
            className="flex items-center justify-between gap-3 p-3"
            style={{ border: '1px solid var(--fx-line)', borderRadius: 4 }}
          >
            <div className="min-w-0">
              <p style={{ fontSize: 13, fontWeight: 700 }}>You reviewed this product</p>
              <div className="flex items-center gap-2 mt-1">
                <StarRating value={myReview.rating} size={12} />
                {myReview.title && (
                  <span className="truncate" style={{ fontSize: 13, color: 'var(--fx-ink-2)' }}>
                    {myReview.title}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => setShowWrite(true)} className="p-2" aria-label="Edit your review">
                <Pencil size={15} style={{ color: 'var(--fx-ink-2)' }} />
              </button>
              <button onClick={() => handleDelete(myReview)} className="p-2" aria-label="Delete your review">
                <Trash2 size={15} style={{ color: 'var(--fx-ink-2)' }} />
              </button>
            </div>
          </div>
        ) : (
          <button type="button" onClick={handleWriteClick} className="fx-btn fx-btn-ghost px-6">
            Write a Review
          </button>
        )}

        {/* Say why the button will not help, rather than letting them find out
            after writing one. */}
        {isAuthenticated && eligibility && !eligibility.can_review && !eligibility.has_reviewed && (
          <p className="mt-2" style={{ fontSize: 12, color: 'var(--fx-ink-3)' }}>
            Only customers who have received this product can review it.
          </p>
        )}
      </div>

      {/* ---- Filters ---- */}
      {total > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-5">
          {starFilter && (
            <button
              type="button"
              onClick={() => setStarFilter(null)}
              className="px-2.5 py-1"
              style={{ border: '1px solid var(--fx-pink)', borderRadius: 2, fontSize: 12, color: 'var(--fx-pink)' }}
            >
              {starFilter} star ×
            </button>
          )}

          {summary?.with_photos > 0 && (
            <button
              type="button"
              onClick={() => setPhotosOnly((v) => !v)}
              className="flex items-center gap-1.5 px-2.5 py-1"
              style={{
                border: `1px solid ${photosOnly ? 'var(--fx-pink)' : 'var(--fx-line)'}`,
                borderRadius: 2,
                fontSize: 12,
                color: photosOnly ? 'var(--fx-pink)' : 'var(--fx-ink-2)',
              }}
            >
              <ImageIcon size={12} />
              With photos ({summary.with_photos})
            </button>
          )}

          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="ml-auto px-2 py-1 outline-none"
            style={{ border: '1px solid var(--fx-line)', borderRadius: 2, fontSize: 13 }}
          >
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      )}

      {/* ---- List ---- */}
      {loading && reviews.length === 0 && (
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-3 bg-[#f5f5f6] w-32 animate-pulse" />
              <div className="h-3 bg-[#f5f5f6] w-full animate-pulse" />
              <div className="h-3 bg-[#f5f5f6] w-2/3 animate-pulse" />
            </div>
          ))}
        </div>
      )}

      {!loading && reviews.length === 0 && (
        <p className="py-8 text-center" style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>
          {total === 0
            ? 'No reviews yet. Be the first to review this product.'
            : 'No reviews match this filter.'}
        </p>
      )}

      <div className="space-y-6">
        {reviews.map((review) => (
          <article key={review.id} className="pb-6" style={{ borderBottom: '1px solid var(--fx-line)' }}>
            <div className="flex items-center gap-2 flex-wrap">
              <StarRating value={review.rating} size={13} />
              {review.title && (
                <span style={{ fontSize: 14, fontWeight: 700 }}>{review.title}</span>
              )}
            </div>

            {review.body && (
              <p className="mt-2" style={{ fontSize: 14, color: 'var(--fx-ink-2)', lineHeight: 1.6 }}>
                {review.body}
              </p>
            )}

            {review.images?.length > 0 && (
              <div className="flex gap-2 mt-3 flex-wrap">
                {review.images.map((img, i) => (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => openPhotos(review, i)}
                    className="overflow-hidden"
                    style={{ width: 64, height: 64, borderRadius: 4, border: '1px solid var(--fx-line)' }}
                  >
                    <img
                      src={resolveProductImageUrl(img.thumbnail_url)}
                      alt={`From ${review.author}'s review`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )}

            <div className="flex items-center gap-3 mt-3 flex-wrap" style={{ fontSize: 12, color: 'var(--fx-ink-3)' }}>
              <span style={{ fontWeight: 700, color: 'var(--fx-ink-2)' }}>{review.author}</span>

              {review.is_verified_purchase && (
                <span className="inline-flex items-center gap-1" style={{ color: 'var(--fx-green)' }}>
                  <BadgeCheck size={13} />
                  Verified Purchase
                </span>
              )}

              {review.variant_name && <span>{review.variant_name}</span>}

              {review.created_at && (
                <span>
                  {new Date(review.created_at).toLocaleDateString('en-IN', {
                    day: 'numeric', month: 'short', year: 'numeric',
                  })}
                </span>
              )}

              <button
                type="button"
                onClick={() => handleHelpful(review)}
                disabled={helpfulVoted[review.id]}
                className="ml-auto inline-flex items-center gap-1.5"
                style={{ color: helpfulVoted[review.id] ? 'var(--fx-pink)' : 'var(--fx-ink-3)' }}
              >
                <ThumbsUp size={13} />
                Helpful{review.helpful_count > 0 ? ` (${review.helpful_count})` : ''}
              </button>
            </div>
          </article>
        ))}
      </div>

      {page < lastPage && (
        <button
          type="button"
          onClick={() => load(page + 1, true)}
          disabled={loading}
          className="fx-btn fx-btn-ghost w-full mt-6"
        >
          {loading ? 'Loading…' : 'Load more reviews'}
        </button>
      )}

      <WriteReviewModal
        isOpen={showWrite}
        onClose={() => setShowWrite(false)}
        product={product}
        existingReview={myReview}
        onSaved={handleSaved}
        showToast={showToast}
        isDesktop={isDesktop}
      />

      {lightbox && (
        <MediaLightbox
          media={lightbox.media}
          startIndex={lightbox.index}
          isOpen
          onClose={() => setLightbox(null)}
          productName={product?.name}
          isDesktop={isDesktop}
        />
      )}
    </section>
  );
};

export default ProductReviews;
